import * as React from 'react';
import { Box, Flex, Icon, Text } from '@ttoss/ui';
import {
  Form,
  FormFieldInput,
  FormFieldRadio,
  useForm,
  yup,
  yupResolver,
} from '@ttoss/forms';
import { HeaderRegister } from '../../components/HeaderRegister/HeaderRegister';
import { useAuth } from '@ttoss/react-auth';
import { useCurrentLocation } from 'src/providers/CurrentLocationProvider/CurrentLocationProvider';
import { useNavigate, useParams } from 'react-router-dom';
import { useProperty } from './useProperty';
import { useSiflorApi } from 'src/providers/SiflorApiProvider/useSiflorApi';
import Button from '../../components/Button/Button';

const schema = yup.object({
  latitude: yup.string().required('É necessário selecionar sua localização'),
  longitude: yup.string().required('É necessário selecionar sua localização'),
  name: yup.string().required('O campo Nome da Propriedade é obrigatório'),
  showOnGooglePlace: yup
    .string()
    .required('É necessário selecionar uma das opções'),
  photoURL: yup.string(),
  operationRadius: yup
    .string()
    .required('É necessário selecionar seu raio de atuação'),
});

type PropertyFormFields = yup.InferType<typeof schema>;

export const PropertyForm = () => {
  const { propertyId } = useParams();

  const { property: initialProperty } = useProperty({ propertyId });

  const { user } = useAuth();

  const { siflorApi } = useSiflorApi();

  const navigate = useNavigate();

  const { latLng } = useCurrentLocation();

  React.useEffect(() => {
    if (latLng || propertyId) {
      return;
    }

    navigate('/');
  }, [latLng, navigate, propertyId]);

  const formMethods = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
  });

  const { reset, watch } = formMethods;

  /**
   * Set initial values.
   */
  React.useEffect(() => {
    if (latLng) {
      reset({
        latitude: latLng.lat.toString(),
        longitude: latLng.lng.toString(),
      });
    }

    if (initialProperty) {
      reset(initialProperty);
    }
  }, [reset, latLng, initialProperty]);

  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async (data: PropertyFormFields) => {
    setError(null);

    const property = {
      latitude: Number(data.latitude),
      longitude: Number(data.longitude),
      name: data.name,
      address: 'Brazil',
      phoneNumber: '(99) 9 9999-9999',
      email: 'joaodasneves@dominio.com',
      showOnGooglePlace: data.showOnGooglePlace,
      operationRadius: data.operationRadius,
      photoURL:
        'https://tt-sandbox-general-purpose.s3.amazonaws.com/siflor/property-perfil-example.png',
    };

    try {
      if (propertyId) {
        await siflorApi.updateProperty(propertyId, property);

        navigate(`/propriedade/${propertyId}`);
      } else {
        const { createProperty } = await siflorApi.createProperty(
          user.id,
          property
        );

        if (createProperty.id) {
          navigate(`/propriedade/${createProperty.id}`);
        } else {
          navigate(`/minhas-propriedades`);
        }
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const latitude = watch('latitude');

  const longitude = watch('longitude');

  return (
    <Flex sx={{ flexDirection: 'column' }}>
      <HeaderRegister
        title={
          <Flex sx={{ flexDirection: 'column', alignItems: 'center' }}>
            <Text sx={{ color: 'background', fontSize: 3 }}>Cadastrando</Text>
            <Text sx={{ color: 'background', fontSize: 4 }}>
              {latitude}, {longitude}
            </Text>
          </Flex>
        }
        hideMenu
      />

      <Form
        sx={{
          paddingX: 6,
          paddingY: 8,
          gap: 7,
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '800px',
          width: '100%',
          alignSelf: ['unset', 'center'],
        }}
        {...formMethods}
        onSubmit={handleSubmit}
      >
        <FormFieldInput name="name" label="Nome da propriedade*:" />

        <Box
          sx={{
            'div>div': {
              flexDirection: 'row',
              display: 'flex',
              width: 'fit-content',
              gap: 7,
            },
          }}
        >
          <FormFieldRadio
            name="showOnGooglePlace"
            label="Deseja que sua propriedade apareça no Google Place?"
            options={[
              { value: 'yes', label: 'Sim' },
              { value: 'no', label: 'Não' },
            ]}
          />
        </Box>

        <FormFieldInput
          name="image"
          label="Deseja cadastrar uma foto da propriedade?"
        />

        <Box
          sx={{
            'div>label:not(:has(input[type="radio"]))': {
              marginBottom: 7,
              color: 'dark',
            },
            'div>label:has(input[type="radio"])': {
              marginBottom: 6,
            },
          }}
        >
          <FormFieldRadio
            name="operationRadius"
            label="Defina seu raio de atuação"
            options={[
              { value: '50', label: 'Até 50km' },
              { value: '100', label: 'Até 100km' },
              { value: '200', label: 'Até 200km' },
              { value: '250', label: 'Até 250km' },
              { value: '300', label: 'Até 300km' },
            ]}
          />
        </Box>

        {propertyId && (
          <Flex
            sx={{
              marginY: 9,
              alignItems: 'center',
              gap: 5,
              alignSelf: 'center',
              cursor: 'pointer',
            }}
            role="button"
          >
            <Icon
              sx={{ fontSize: 4, lineHeight: 0 }}
              icon="ant-design:plus-circle-outlined"
            />

            <Text sx={{ fontSize: 2 }}>
              Adicional nova produção para receber contatos
            </Text>
          </Flex>
        )}

        {error && <Text sx={{ color: 'error' }}>{error}</Text>}

        <Flex sx={{ flexDirection: 'column', gap: 6 }}>
          <Button
            color="primary"
            type="button"
            outlined
            onClick={() => {
              navigate('/minhas-propriedades');
            }}
          >
            Cancelar
          </Button>

          <Button type="submit">Pronto!</Button>
        </Flex>
      </Form>
    </Flex>
  );
};
